/* ─────────────────────────────────────────────────────────────────────────
   KIMDYMA · eb-shell.js — the shell: overlays, header, megas, drawer,
   announcement, search, region pickers, footer accordion. v4.0

   Replaces v1.0's eb-overlay.js, which called showModal() and never added
   .is-open — the class every v3.4 overlay transitions on.

   OVERLAYS ARE NATIVE <dialog>. The browser does the hard parts — focus trap,
   inert background, Escape, top layer. This file adds the one thing it does
   not: the entrance and the exit. Open: showModal(), a reflow, then .is-open.
   Close: remove .is-open, wait out the transition, then close(). Escape is
   intercepted (`cancel`) so it takes the same road out. The scroll lock is
   derived from the DOM — any dialog [open] — never from a counter.

   Everything is additive: with this file gone the drawer is unreachable but
   the nav links still work, search still submits its form, the region
   picker still opens as a <details>, and every footer column stays open.
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.ebShellLoaded) return;
  window.ebShellLoaded = true;

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
  var slice = function (l) { return Array.prototype.slice.call(l); };
  function motionOff() { return reduce.matches || root.getAttribute('data-motion') === 'off'; }
  var EXIT = 460;

  /* ══ OVERLAYS ══════════════════════════════════════════════════════════ */
  function syncLock() {
    /* :not(.eb-corner) — the corner card is open NON-MODALLY and must not
       lock the page. Without the exclusion it kept the scroll locked after
       the drawer that actually locked it had closed. */
    var locked = !!document.querySelector('dialog[open].eb-drawer, dialog[open].eb-dialog:not(.eb-corner), dialog[open].eb-sheet, dialog[open].eb-navdrawer, dialog[open].eb-search');
    root.classList.toggle('is-locked', locked);
    document.body.classList.toggle('is-locked', locked);
  }
  /* A CORNER CARD IS NOT A MODAL. Above the phone breakpoint the newsletter
     sits in the bottom-right corner and the page goes on working around it:
     opened with show(), no backdrop, nothing made inert, no scroll lock. On a
     phone there is no corner, so it is the sheet and it is modal like
     everything else. */
  function openDialog(d, trigger) {
    if (!d || d.open) return;
    var corner = d.hasAttribute('data-eb-newsletter') &&
      window.matchMedia('(min-width: 641px)').matches;
    d.classList.toggle('eb-corner', corner);
    if (typeof d.showModal !== 'function') { d.setAttribute('open', ''); }
    else if (corner) d.show();
    else d.showModal();
    d._ebTrigger = trigger || document.activeElement;
    void d.offsetWidth;
    d.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    syncLock();
    var first = d.querySelector('[autofocus], input:not([type=hidden]), button, a[href]');
    if (first && d.hasAttribute('data-eb-search')) { var inp = d.querySelector('[data-eb-search-input]'); if (inp) inp.focus(); }
    d.dispatchEvent(new CustomEvent('eb:overlay:open', { bubbles: true }));
  }
  function closeDialog(d) {
    if (!d || !d.open || d._ebClosing) return;
    d._ebClosing = true;
    d.classList.remove('is-open');
    var trigger = d._ebTrigger;
    window.setTimeout(function () {
      d._ebClosing = false;
      if (d.open) d.close();
      syncLock();
      if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', 'false');
      if (trigger && trigger.focus) trigger.focus();
      d.dispatchEvent(new CustomEvent('eb:overlay:close', { bubbles: true }));
    }, motionOff() ? 0 : EXIT);
  }
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-eb-open]');
    if (opener) {
      /* Resolve FIRST. A cart button is a real link to /cart and has to stay
         one when the drawer is switched off — preventDefault before knowing
         whether the dialog exists is how a control ends up doing nothing. */
      var target = document.querySelector(opener.getAttribute('data-eb-open'));
      if (!target) return;
      e.preventDefault();
      openDialog(target, opener);
      return;
    }
    var closer = e.target.closest('[data-eb-close]');
    if (closer) { e.preventDefault(); closeDialog(closer.closest('dialog')); return; }
    // A click on the dialog element itself (not its panel) is a backdrop hit.
    if (e.target.tagName === 'DIALOG' && e.target.open) closeDialog(e.target);
  });
  document.addEventListener('cancel', function (e) {
    if (e.target.tagName !== 'DIALOG') return;
    e.preventDefault();
    closeDialog(e.target);
  }, true);
  window.ebOverlay = { open: openDialog, close: closeDialog };

  /* ══ NEWSLETTER DIALOG ═════════════════════════════════════════════════
     The markup and the settings existed and NOTHING OPENED IT — the section
     read as "made" and was not. This is the missing half.

     Two ways in, whichever comes first: the Nth page view, or a dwell on this
     one. Views are counted in localStorage because a first-time visitor who is
     still deciding has not asked for anything yet; the dwell is what catches
     the visitor who arrives on one long page and stays.

     One refusal is enough for thirty days. A dismissal is an answer, and a
     dialog that asks again next Tuesday is a dialog that gets blocked.

     Storage can throw — private windows, blocked site data — so every read and
     write is guarded and the failure mode is "do not interrupt anyone", which
     is the right way round. */
  var KEY_SEEN = 'eb:nl:seen', KEY_VIEWS = 'eb:nl:views';
  var KEY_NUDGE = 'eb:nl:nudge', KEY_JOINED = 'eb:nl:joined';
  var DAY = 24 * 60 * 60 * 1000, DAYS_30 = 30 * DAY;

  function store(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function read(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }

  /* ══ THE OFFER, IN TWO STATES ══════════════════════════════════════════
     One overlay, not two. The dialog is the offer open and the bubble is the
     offer folded up in a corner, and the visitor moves between them.

     ONE OR THE OTHER, NEVER BOTH. The bubble IS the dialog folded up, so the
     two are never on screen together: the dialog opening takes the bubble
     away, and the dialog closing puts it back. That rule lives in the two
     overlay events below rather than at each place that opens something,
     because the editor opened the dialog by a road that skipped those places
     and showed both at once.

     CLOSING IS NOT REFUSING. Shutting a dialog to read the page is not a no,
     so closing collapses to the bubble and writes nothing. The cross ON THE
     BUBBLE is the no, and that is the click that buys the thirty days of
     quiet. A shop that treats the first as the second has stopped listening.

     THE ELEMENTS ARE LOOKED UP WHEN NEEDED, NEVER HELD. The editor re-renders
     the section on every settings change, and a reference taken at load is
     then a node no longer in the page — the bubble stopped answering its own
     click after the first edit.

     Storage can throw — private windows, blocked site data — so every read
     and write is guarded and the failure mode is "do not interrupt anyone",
     which is the right way round. */
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var nlDialog = function () { return document.querySelector('[data-eb-newsletter]'); };
  var nlBubble = function () { return document.querySelector('[data-eb-bubble]'); };
  var isNewsletter = function (el) { return !!(el && el.hasAttribute && el.hasAttribute('data-eb-newsletter')); };

  /* THE PEEK. On a phone the bubble is a tab tucked against the edge, and a
     tab reading "€50" says nothing about what it is. So the first time it
     appears it slides out to say so, and tucks back after five seconds or
     the first real scroll — once per visitor, and never again on the same
     page after the sheet has been closed. With reduced motion it simply
     arrives tucked. */
  var KEY_PEEKED = 'eb:nl:peeked';
  var phone = window.matchMedia('(max-width: 640px)');
  var peekedHere = false;
  function peek(b) {
    if (peekedHere || !phone.matches || motionOff()) return;
    if (!designMode && read(KEY_PEEKED)) return;
    peekedHere = true;
    if (!designMode) store(KEY_PEEKED, '1');
    b.classList.add('is-peek');
    var y0 = window.scrollY, timer;
    var tuck = function () {
      b.classList.remove('is-peek');
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
    var onScroll = function () { if (Math.abs(window.scrollY - y0) > 40) tuck(); };
    timer = setTimeout(tuck, 5000);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function showBubble() {
    var b = nlBubble(), d = nlDialog();
    if (!b || (d && d.open)) return;
    if (!designMode && read(KEY_JOINED)) return;
    b.hidden = false;
    peek(b);
  }
  function hideBubble() {
    var b = nlBubble();
    if (!b) return;
    b.hidden = true;
    b.classList.remove('is-peek');
  }

  document.addEventListener('click', function (e) {
    /* NO THANKS, on the phone's sheet: the same answer as the bubble's
       cross, so it buys the same thirty days — and the sheet closes without
       folding back to a tab the visitor has just refused. */
    var no = e.target.closest('[data-eb-decline]');
    if (no) {
      var sheet = no.closest('dialog');
      if (!designMode) store(KEY_SEEN, Date.now());
      if (sheet) { sheet._ebDeclined = true; closeDialog(sheet); }
      return;
    }
    var b = e.target.closest('[data-eb-bubble]');
    if (!b) return;
    if (e.target.closest('[data-eb-bubble-dismiss]')) {
      b.hidden = true;
      if (!designMode) store(KEY_SEEN, Date.now());
      return;
    }
    var opener = e.target.closest('[data-eb-bubble-open]');
    var d = nlDialog();
    if (opener && d) openDialog(d, opener);
  });

  document.addEventListener('eb:overlay:open', function (e) {
    if (isNewsletter(e.target)) hideBubble();
  });
  document.addEventListener('eb:overlay:close', function (e) {
    if (!isNewsletter(e.target)) return;
    if (e.target._ebDeclined) {
      e.target._ebDeclined = false;
      hideBubble();
      return;
    }
    /* Collapse if there is somewhere to collapse to; otherwise a close is
       the only answer the visitor can give, so it has to count as one. */
    if (nlBubble()) showBubble();
    else if (!designMode) store(KEY_SEEN, Date.now());
  });

  var nlStart = nlDialog();
  if (!designMode && nlStart && nlStart.getAttribute('data-armed') === 'true') {
    var answered = parseInt(read(KEY_SEEN) || '0', 10);
    var quiet = answered && Date.now() - answered < DAYS_30;

    if (!read(KEY_JOINED) && !quiet) {
      /* THE BUBBLE FIRST, if the shop has chosen to wait rather than
         interrupt. Nothing is timed in that case: it is simply there, and
         the dialog waits for the bubble to be pressed. With the bubble
         switched off there is nothing to wait in, so the dialog's own
         trigger applies instead of nothing ever appearing. */
      if (nlStart.getAttribute('data-opens') === 'bubble' && nlBubble()) {
        showBubble();
      } else {
        var views = parseInt(read(KEY_VIEWS) || '0', 10) + 1;
        store(KEY_VIEWS, views);
        var wanted = parseInt(nlStart.getAttribute('data-views') || '2', 10);
        var dwell = parseInt(nlStart.getAttribute('data-delay') || '40', 10) * 1000;
        var fired = false;

        var raiseNewsletter = function () {
          var d = nlDialog();
          if (fired || !d || d.open) return;
          // Never on top of something the visitor opened themselves.
          if (document.querySelector('dialog[open]')) return;
          fired = true;
          openDialog(d);
        };

        if (views >= wanted) raiseNewsletter();
        else setTimeout(raiseNewsletter, dwell);
      }
    }
  }

  /* ══ THE EDITOR CAN SEE THE OVERLAYS ═══════════════════════════════════
     An overlay draws nothing until something opens it, and in the theme editor
     nothing does: selecting "Cart drawer" or "Newsletter popup" in the sidebar
     highlighted a section that showed no change at all, which is indis-
     tinguishable from a section that was never built. Shopify fires
     section:select on the section's own wrapper, so selecting one opens
     whatever it holds and deselecting closes it. designMode only. */
  if (window.Shopify && window.Shopify.designMode) {
    function show(el, on) {
      if (!el) return;
      if (el.tagName === 'DIALOG') {
        if (on) openDialog(el); else closeDialog(el);
      } else {
        el.hidden = !on;                       // the cookie bar is a bar, not a dialog
      }
    }
    /* THE NEWSLETTER IS PREVIEWED THE WAY IT ARRIVES. "The bubble only" puts
       the bubble up and leaves the dialog shut — pressing the bubble opens
       the dialog here exactly as it does in the shop, and closing it folds
       back. "The dialog" opens the dialog. It used to open both at once, a
       state no visitor can ever reach, previewed as though it were the
       design. */
    var nlArrives = function () {
      var d = nlDialog();
      return d && d.getAttribute('data-opens') === 'bubble' && nlBubble() ? 'bubble' : 'dialog';
    };
    var presentNewsletter = function (view) {
      var d = nlDialog();
      if (!d) return;
      if (view === 'bubble') {
        if (d.open) closeDialog(d);          // the close event puts the bubble up
        else showBubble();
      } else {
        openDialog(d);                       // the open event takes the bubble away
      }
    };
    var nlMode = nlArrives();
    var nlView = null;
    document.addEventListener('eb:overlay:open', function (e) { if (isNewsletter(e.target)) nlView = 'dialog'; });
    document.addEventListener('eb:overlay:close', function (e) { if (isNewsletter(e.target)) nlView = 'bubble'; });

    function editorShow(section, on) {
      if (!section || !section.querySelector) return;
      /* OVERLAY SECTIONS ONLY. This used to open the first <dialog> in
         whatever section was selected, and the header's first dialog is the
         PHONE NAV DRAWER — so selecting "Header" in the sidebar threw the
         mobile menu over a desktop preview. The overlays section is the one
         that exists to be shown; a drawer that belongs to a breakpoint is not
         something the editor should force open at any width. */
      if (!section.classList || !section.classList.contains('eb-overlays')) return;
      if (section.querySelector('[data-eb-newsletter]')) {
        if (on) presentNewsletter(nlArrives());
        else { var d = nlDialog(); if (d && d.open) closeDialog(d); }
        return;
      }
      show(section.querySelector('dialog') || section.querySelector('[data-eb-consent]'), on);
    }
    document.addEventListener('shopify:section:select', function (e) { editorShow(e.target, true); });
    document.addEventListener('shopify:section:deselect', function (e) { editorShow(e.target, false); });

    /* The editor RE-RENDERS a section on every settings change, which throws
       away the open dialog. Putting back what the merchant was looking at is
       what makes editing the newsletter's copy possible at all — otherwise it
       vanishes on the first keystroke and the merchant is typing at nothing.
       A change to "What arrives first" is the exception: then the new arrival
       is what they want to see. */
    document.addEventListener('shopify:section:load', function (e) {
      if (!e.target.classList.contains('eb-overlays')) return;
      if (e.target.querySelector('[data-eb-newsletter]')) {
        var mode = nlArrives();
        presentNewsletter(mode !== nlMode ? mode : (nlView || mode));
        nlMode = mode;
        return;
      }
      editorShow(e.target, true);
    });

    /* The newsletter is the one that is otherwise UNREACHABLE in the editor:
       it is gated on a page count and a thirty-day dismissal, so a merchant
       editing its copy would have to clear storage and reload twice to see a
       single word change. In design mode it simply stands in its arriving
       state. The drawer and the search overlay are not forced open — they
       would cover the page a merchant is trying to design, and selecting
       them opens them. */
    presentNewsletter(nlMode);
  }

  /* ══ HEADER STATES ═════════════════════════════════════════════════════ */
  var header = document.querySelector('[data-eb-header]');
  if (header) {
    var lastY = window.scrollY, ticking = false;
    function paintHeader() {
      ticking = false;
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      /* SCROLLING DOWN IS READING; SCROLLING UP IS LOOKING FOR SOMETHING.
         So the header leaves on the way down — the page is what the reader
         asked for, not the menu — and comes back the instant the direction
         reverses, pinned to the top where they are reaching for it. At every
         width: it was tablet-and-below for one release, which meant the
         desktop kept a bar the reader had already scrolled past.

         A mega menu holds it open: taking the header away under an open
         panel would drag the panel off with it. */
      if (!header.classList.contains('has-mega')) {
        header.classList.toggle('is-hidden', y > lastY && y > 120);
      } else header.classList.remove('is-hidden');
      lastY = y;
    }
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(paintHeader); } }, { passive: true });
    paintHeader();

    /* ── --header-h IS PUBLISHED, NOT GUESSED ──────────────────────────────
       The buy column on a product page pins beneath the header, and that
       section reserves exactly that much room at its top so the column starts
       level with the photograph. Both read --header-h.

       It is measured rather than written down because the height is not one
       number: it drops on narrow viewports, and it grows with whatever logo
       the shop uploaded. The sticky element is the SECTION WRAPPER (see
       shell.css), so that is what gets measured. The CSS keeps a 77px
       fallback — the desktop resting height — for the first paint and for a
       visitor with no JS. */
    var headerBox = header.closest('.shopify-section') || header;
    function publishHeaderHeight() {
      var h = Math.round(headerBox.getBoundingClientRect().height);
      if (h) document.documentElement.style.setProperty('--header-h', h + 'px');
    }
    publishHeaderHeight();
    /* AND AGAIN ON LOAD. The first call can measure 0 — a header inside a
       frame, or one whose logo has not decoded yet, has no box at script time
       — and `if (h)` then leaves the variable unset for good. Everything that
       pins under the header reads it, so an unpublished --header-h is a buy
       bar over the header and a buy column that starts too high. The
       ResizeObserver is the general fix and this is the first-paint one. */
    window.addEventListener('load', publishHeaderHeight);
    if (window.ResizeObserver) new ResizeObserver(publishHeaderHeight).observe(headerBox);
    else window.addEventListener('resize', publishHeaderHeight);
  }

  /* ══ BACK TO TOP ═══════════════════════════════════════════════════════
     Shows on the way down once the top is a screen and a half away — far
     enough that going back is a journey rather than a flick — and hides again
     near the top, where the button would be pointing at the ground under the
     reader's feet.

     It also gets out of the compare tray's way. The tray is a fixed bar along
     the bottom, so while it is open the button rides above it; the height is
     measured rather than assumed, because the tray is 73px on a desktop and
     57px on a phone. */
  var totop = document.querySelector('[data-eb-totop]');
  if (totop) {
    var totopTick = false;
    function paintTotop() {
      totopTick = false;
      var y = window.scrollY;
      totop.classList.toggle('is-on', y > window.innerHeight * 1.5);
      var tray = document.querySelector('.eb-compare-tray.is-open');
      var lift = tray ? Math.round(tray.getBoundingClientRect().height) + 8 : 0;
      document.documentElement.style.setProperty('--totop-lift', lift + 'px');
    }
    window.addEventListener('scroll', function () {
      if (!totopTick) { totopTick = true; requestAnimationFrame(paintTotop); }
    }, { passive: true });
    window.addEventListener('resize', paintTotop);
    /* The tray opens and closes without the page moving, so its own event is
       what tells the button to step up. */
    document.addEventListener('eb:compare:change', function () {
      requestAnimationFrame(paintTotop);
    });
    totop.addEventListener('click', function () {
      window.scrollTo({ top: 0,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
    paintTotop();
  }

  /* ══ MEGA MENUS ════════════════════════════════════════════════════════
     Hover opens on a pointer with intent (a short delay); click toggles
     everywhere; Escape and leaving the header close. One open at a time. */
  var openMega = null, megaTimer = null;
  function showMega(trigger) {
    var panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    if (openMega && openMega.panel === panel) return;
    hideMega(true);
    panel.hidden = false;
    void panel.offsetWidth;
    panel.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    if (header) header.classList.add('has-mega');
    openMega = { panel: panel, trigger: trigger };
  }
  function hideMega(immediate) {
    if (!openMega) return;
    var m = openMega; openMega = null;
    m.panel.classList.remove('is-open');
    m.trigger.setAttribute('aria-expanded', 'false');
    if (header) header.classList.remove('has-mega');
    m.panel.dispatchEvent(new CustomEvent('eb:mega:close', { bubbles: true }));
    window.setTimeout(function () { if (!m.panel.classList.contains('is-open')) m.panel.hidden = true; },
      immediate || motionOff() ? 0 : EXIT);
  }
  function armMegaClose() { window.clearTimeout(megaTimer); megaTimer = window.setTimeout(hideMega, 140); }
  function disarmMegaClose() { window.clearTimeout(megaTimer); }
  slice(document.querySelectorAll('[data-eb-mega]')).forEach(function (t) {
    t.addEventListener('click', function () {
      if (openMega && openMega.trigger === t) hideMega(); else showMega(t);
    });
    t.addEventListener('mouseenter', function () {
      if (!hoverable.matches) return;
      disarmMegaClose();
      megaTimer = window.setTimeout(function () { showMega(t); }, 80);
    });
    t.addEventListener('mouseleave', function () { if (hoverable.matches) armMegaClose(); });
    t.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' && openMega && openMega.trigger === t) {
        e.preventDefault();
        var f = openMega.panel.querySelector('a, button');
        if (f) f.focus();
      }
    });
  });
  slice(document.querySelectorAll('[data-eb-mega-panel]')).forEach(function (p) {
    p.addEventListener('mouseenter', disarmMegaClose);
    p.addEventListener('mouseleave', function () { if (hoverable.matches) armMegaClose(); });
    p.addEventListener('focusout', function (e) {
      if (!p.contains(e.relatedTarget) && !(openMega && e.relatedTarget === openMega.trigger)) armMegaClose();
    });
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') hideMega(); });
  document.addEventListener('click', function (e) {
    if (openMega && !e.target.closest('.eb-mega') && !e.target.closest('[data-eb-mega]')) hideMega();
  });

  /* ══ MEGA · 车型分类 ═════════════════════════════════════════════════════
     左边的分类：悬停或聚焦就换右边那一类的车，点一下照常进分类页（它是链接）。
     只改 hidden 和 aria-current，四类的车都已经在 HTML 里。键盘上下键在分类之间走。 */
  function showCat(a) {
    var box = a.closest('[data-eb-mega-panel]');
    if (!box || a.getAttribute('aria-current') === 'true') return;
    slice(box.querySelectorAll('[data-eb-mcat]')).forEach(function (x) {
      if (x === a) x.setAttribute('aria-current', 'true'); else x.removeAttribute('aria-current');
    });
    var id = a.getAttribute('data-eb-mcat');
    slice(box.querySelectorAll('[data-eb-mcat-panel]')).forEach(function (p) { p.hidden = p.id !== id; });
  }
  document.addEventListener('mouseover', function (e) {
    var a = e.target.closest && e.target.closest('[data-eb-mcat]');
    if (a && hoverable.matches) showCat(a);
  });
  document.addEventListener('focusin', function (e) {
    var a = e.target.closest && e.target.closest('[data-eb-mcat]');
    if (a) showCat(a);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    var a = e.target.closest && e.target.closest('[data-eb-mcat]');
    if (!a) return;
    var all = slice(a.parentNode.querySelectorAll('[data-eb-mcat]'));
    var next = all[(all.indexOf(a) + (e.key === 'ArrowDown' ? 1 : -1) + all.length) % all.length];
    e.preventDefault();
    next.focus();
  });

  /* ══ 手机抽屉 · 分类胶囊 ═══════════════════════════════════════════════════
     手机没有悬停，第一下点击用来切换下面的车，不跳页；去分类页点列表底下的 See all。 */
  document.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest('[data-eb-navcat]');
    if (!chip) return;
    var panel = chip.closest('.eb-navpanel');
    var i = chip.getAttribute('data-eb-navcat');
    slice(panel.querySelectorAll('[data-eb-navcat]')).forEach(function (c) {
      c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
    });
    slice(panel.querySelectorAll('[data-eb-navbikes]')).forEach(function (l) {
      l.hidden = l.getAttribute('data-eb-navbikes') !== i;
    });
    /* 点到半露在边上的胶囊，把它整个滑进来 */
    var row = chip.parentNode, pad = 20;
    var l = chip.offsetLeft - row.scrollLeft, r = l + chip.offsetWidth;
    if (l < pad) row.scrollBy({ left: l - pad, behavior: motionOff() ? 'auto' : 'smooth' });
    else if (r > row.clientWidth - pad) row.scrollBy({ left: r - row.clientWidth + pad, behavior: motionOff() ? 'auto' : 'smooth' });
  });

  /* 胶囊行不出滚动条，用两边渐隐告诉人「这边还有」：左边滑过了才亮左边，右边没到头才亮右边 */
  function navcatsEdges(wrap) {
    var row = wrap.querySelector('[data-eb-navcats]');
    if (!row) return;
    function paint() {
      var max = row.scrollWidth - row.clientWidth;
      wrap.classList.toggle('has-left', row.scrollLeft > 2);
      wrap.classList.toggle('has-right', row.scrollLeft < max - 2);
    }
    row.addEventListener('scroll', paint, { passive: true });
    window.addEventListener('resize', paint);
    /* 抽屉面板打开前宽度是 0，量不准；面板一显示就重画 */
    if ('ResizeObserver' in window) new ResizeObserver(paint).observe(row);
    paint();
  }
  slice(document.querySelectorAll('[data-eb-navcats-wrap]')).forEach(navcatsEdges);

  /* ══ 预售日期过期 ════════════════════════════════════════════════════════
     卡片、菜单里的「Ships 26 Oct」是服务器那天算的，页面会被缓存。访客的今天已经到了
     发货日，就换回原来那句库存（data-after），没有原来那句就整个去掉。 */
  (function () {
    var d = new Date();
    var today = d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    slice(document.querySelectorAll('[data-eb-ships-until]')).forEach(function (el) {
      if (el.getAttribute('data-eb-ships-until') > today) return;
      var after = el.getAttribute('data-after');
      if (after) { el.textContent = after; el.className = 'eb-stock eb-stock--in'; }
      else el.remove();
    });
  })();

  /* ══ ANNOUNCEMENT ══════════════════════════════════════════════════════ */
  slice(document.querySelectorAll('[data-eb-announce]')).forEach(function (bar) {
    var key = 'eb-announce-' + bar.getAttribute('data-eb-announce');
    try { if (sessionStorage.getItem(key)) { bar.classList.add('is-dismissed'); return; } } catch (err) {}
    var slots = slice(bar.querySelectorAll('[data-eb-slot]')), i = 0, timer = null, runTimer = null;
    var every = (parseInt(bar.getAttribute('data-rotate'), 10) || 5) * 1000;
    /* 放不下一行的 slot：内容包进一个 run，量出差多少，设成横移距离。
       速度按距离算（每秒 40px），前后各停 1.2 秒，这一条停留的时间相应拉长。
       字体加载、换屏宽、预计到货日期写进去之后宽度会变，所以每次轮到它时重新量。 */
    var HOLD = 1200, SPEED = 40;
    function measure(slot) {
      var run = slot.querySelector('.eb-announce__run');
      if (!run) {
        run = document.createElement('span'); run.className = 'eb-announce__run';
        while (slot.firstChild) run.appendChild(slot.firstChild);
        slot.appendChild(run);
      }
      /* 先瞬间归位再量，否则退回去也会慢慢滑 */
      run.style.setProperty('--run-dur', '0ms');
      slot.classList.remove('is-long', 'is-running', 'is-still');
      void run.offsetWidth;
      var over = run.scrollWidth - slot.clientWidth;
      if (over <= 2) { slot.ebRun = 0; return; }
      slot.classList.add(motionOff() ? 'is-still' : 'is-long');
      slot.ebRun = motionOff() ? 0 : Math.ceil(over / SPEED * 1000);
      run.style.setProperty('--run', -Math.ceil(over) + 'px');
      run.style.setProperty('--run-dur', slot.ebRun + 'ms');
    }
    function play(slot) {
      window.clearTimeout(runTimer);
      measure(slot);
      if (!slot.ebRun) return 0;
      runTimer = window.setTimeout(function () { slot.classList.add('is-running'); }, HOLD);
      return HOLD + slot.ebRun + HOLD;
    }
    function go(n) {
      slots[i].classList.remove('is-current', 'is-running'); slots[i].setAttribute('aria-hidden', 'true');
      i = (n + slots.length) % slots.length;
      slots[i].classList.add('is-current'); slots[i].removeAttribute('aria-hidden');
      return play(slots[i]);
    }
    function next(wait) {
      window.clearTimeout(timer);
      if (slots.length < 2 || motionOff()) return;
      timer = window.setTimeout(function () { next(go(i + 1)); }, Math.max(every, wait || 0));
    }
    /* 只有一条时没有轮播，长句自己隔一会儿再走一遍 */
    function solo() {
      stop();
      var w = play(slots[0]);
      if (w) timer = window.setTimeout(solo, w + every);
    }
    function start() { if (slots.length > 1) next(play(slots[i])); else solo(); }
    function stop() { window.clearTimeout(timer); }
    start();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(start);
    /* 转屏、改窗口宽度后，当前这条重新量、从头走 */
    var rz = null;
    window.addEventListener('resize', function () { window.clearTimeout(rz); rz = window.setTimeout(start, 250); });
    bar.addEventListener('mouseenter', stop); bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop); bar.addEventListener('focusout', start);
    bar.addEventListener('click', function (e) {
      if (e.target.closest('[data-eb-announce-prev]')) go(i - 1);
      else if (e.target.closest('[data-eb-announce-next]')) go(i + 1);
      else if (e.target.closest('[data-eb-announce-close]')) {
        bar.classList.add('is-dismissed');
        try { sessionStorage.setItem(key, '1'); } catch (err) {}
      }
    });
    // Countdown: rendered here, in the visitor's zone, seconds the finest unit.
    slice(bar.querySelectorAll('[data-eb-countdown]')).forEach(function (cd) {
      var end = Date.parse(cd.getAttribute('data-eb-countdown'));
      if (isNaN(end)) return;
      var warn = (parseFloat(cd.getAttribute('data-warn-hours')) || 1) * 3600;
      var count = cd.querySelector('[data-eb-count]');
      var pad = function (n) { return String(n).padStart(2, '0'); };
      function tick() {
        var s = Math.max(0, Math.floor((end - Date.now()) / 1000));
        var parts = { d: Math.floor(s / 86400), h: Math.floor(s % 86400 / 3600), m: Math.floor(s % 3600 / 60), s: s % 60 };
        slice(cd.querySelectorAll('[data-cd]')).forEach(function (el) { el.textContent = pad(parts[el.getAttribute('data-cd')]); });
        if (count) count.classList.toggle('is-urgent', s < warn);
        if (s <= 0) { window.clearInterval(t); var slot = cd.closest('[data-eb-slot]'); if (slot && slots.length > 1) { slot.remove(); slots = slice(bar.querySelectorAll('[data-eb-slot]')); i = 0; slots[0].classList.add('is-current'); } }
      }
      var t = window.setInterval(tick, 1000); tick();
    });
  });

  /* ══ NAV DRAWER PANELS ═════════════════════════════════════════════════ */
  /* THE FOCUS MUST NOT SCROLL. The next panel is still parked at
     translateX(100%) when its first row takes focus, and focusing an element
     outside an overflow:hidden box SCROLLS that box to reveal it — the panels
     container jumped 375px sideways, the slide then finished at 0, and the
     whole level sat one screen to the left of the drawer: a blank panel with
     the head and the foot intact. Focus without scrolling, and put the
     container back at 0 in case anything else moved it.

     BOUND PER DRAWER, AND AGAIN WHEN THE EDITOR RE-RENDERS THE HEADER. The
     binding ran once at page load, so after any change to the header section
     the drawer that replaced it had no panel controller at all. */
  function bindNavDrawer(d) {
    if (d.ebNavBound) return;
    d.ebNavBound = true;
    var box = d.querySelector('.eb-navdrawer__panels');
    /* Focus follows the panel only for a KEYBOARD user, who would otherwise be
       left on a button that has slid out of view. A tap has no such problem,
       and moving focus after one drew the focus ring round the back row as if
       it had been selected. A keyboard "click" arrives with detail 0. */
    function go(to, byKeyboard) {
      slice(d.querySelectorAll('[data-eb-panel]')).forEach(function (p) {
        var n = parseInt(p.getAttribute('data-eb-panel'), 10);
        p.classList.toggle('is-current', n === to);
        p.classList.toggle('is-previous', n < to);
      });
      if (byKeyboard) {
        var f = d.querySelector('[data-eb-panel="' + to + '"] a, [data-eb-panel="' + to + '"] button');
        if (f) {
          try { f.focus({ preventScroll: true }); } catch (err) { f.focus(); }
        }
      }
      if (box) { box.scrollLeft = 0; box.scrollTop = 0; }
    }
    d.addEventListener('click', function (e) {
      var b = e.target.closest('[data-eb-go]');
      if (b) go(parseInt(b.getAttribute('data-eb-go'), 10), e.detail === 0);
    });
    d.addEventListener('eb:overlay:close', function () { window.setTimeout(function () { go(0); }, EXIT); });
  }
  slice(document.querySelectorAll('[data-eb-navdrawer]')).forEach(bindNavDrawer);
  document.addEventListener('shopify:section:load', function (e) {
    slice(e.target.querySelectorAll('[data-eb-navdrawer]')).forEach(bindNavDrawer);
  });

  /* ══ SEARCH ════════════════════════════════════════════════════════════
     Live results from Shopify's predictive API, debounced, cancellable, and
     never rendered out of order. Recent terms stay in this browser. */
  slice(document.querySelectorAll('[data-eb-search]')).forEach(function (d) {
    var input = d.querySelector('[data-eb-search-input]');
    var form = d.querySelector('[data-eb-search-form]');
    var results = d.querySelector('[data-eb-search-results]');
    var deflt = d.querySelector('[data-eb-search-default]');
    var recentBox = d.querySelector('[data-eb-search-recent]');
    var recentList = d.querySelector('[data-eb-search-recent-list]');
    var strings = {};
    try { strings = JSON.parse(d.querySelector('[data-eb-search-strings]').textContent); } catch (err) {}
    var KEY = 'eb-search-recent';
    function recent() { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (err) { return []; } }
    function remember(q) {
      var r = recent().filter(function (x) { return x !== q; }); r.unshift(q);
      try { localStorage.setItem(KEY, JSON.stringify(r.slice(0, 5))); } catch (err) {}
    }
    function paintRecent() {
      if (!recentBox || !recentList) return;
      var r = recent();
      recentBox.hidden = !r.length;
      recentList.innerHTML = r.map(function (q) {
        return '<a class="eb-search__term" href="' + strings.url + '?q=' + encodeURIComponent(q) + '">' +
          '<svg class="eb-icon eb-icon--sm" aria-hidden="true"><use href="#i-clock"/></svg>' + esc(q) + '</a>';
      }).join('');
    }
    function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
    d.addEventListener('click', function (e) {
      if (e.target.closest('[data-eb-search-clear]')) { try { localStorage.removeItem(KEY); } catch (err) {} paintRecent(); }
    });
    paintRecent();
    if (!input || !results) return;

    var seq = 0, timer = null, ctrl = null;
    function row(item, kind) {
      var img = item.featured_image && item.featured_image.url ? item.featured_image.url : (item.image || '');
      var thumb = img ? '<span class="eb-menu__thumb"><img src="' + esc(img) + '" alt="" loading="lazy"></span>' : '';
      var meta = kind === 'products' && item.price ? '<span class="eb-menu__meta">' + esc(item.price) + '</span>' : '';
      return '<a class="eb-menu__row" href="' + esc(item.url) + '">' + thumb + esc(item.title) + meta + '</a>';
    }
    function render(q, data) {
      var r = (data && data.resources && data.resources.results) || {};
      var html = '';
      ['products', 'articles', 'pages'].forEach(function (k) {
        if (!r[k] || !r[k].length) return;
        html += '<div class="eb-search__group"><div class="eb-search__title">' + esc(strings[k] || k) + '</div>' +
          r[k].map(function (it) { return row(it, k); }).join('') + '</div>';
      });
      if (!html) html = '<p class="eb-search__empty">' + esc((strings.empty || '').replace('@@', q)) + '</p>';
      else html += '<a class="eb-link" href="' + strings.url + '?q=' + encodeURIComponent(q) + '">' + esc((strings.all || '').replace('@@', q)) +
        ' <svg class="eb-icon eb-icon--xs" aria-hidden="true"><use href="#i-arrow-right"/></svg></a>';
      results.innerHTML = html;
      results.hidden = false;
      if (deflt) deflt.hidden = true;
    }
    function reset() { results.hidden = true; results.innerHTML = ''; if (deflt) deflt.hidden = false; }
    input.addEventListener('input', function () {
      var q = input.value.trim();
      window.clearTimeout(timer);
      if (ctrl) ctrl.abort();
      if (q.length < 2) { reset(); return; }
      timer = window.setTimeout(function () {
        var mine = ++seq;
        ctrl = ('AbortController' in window) ? new AbortController() : null;
        var url = (strings.predictive || '/search/suggest') + '?q=' + encodeURIComponent(q) +
          '&resources[type]=' + (strings.types || 'product,article,page') +
          '&resources[limit]=' + (strings.limit || 5) +
          '&resources[options][unavailable_products]=last';
        fetch(url, { headers: { Accept: 'application/json' }, signal: ctrl ? ctrl.signal : undefined })
          .then(function (res) { return res.json(); })
          .then(function (data) { if (mine === seq) render(q, data); })
          .catch(function () {});
      }, 220);
    });
    if (form) form.addEventListener('submit', function () { var q = input.value.trim(); if (q) remember(q); });
    d.addEventListener('eb:overlay:close', function () { input.value = ''; reset(); });
  });

  /* ══ REGION PICKERS · <details>, closed by a click elsewhere or Escape ═ */
  document.addEventListener('click', function (e) {
    slice(document.querySelectorAll('[data-eb-region][open]')).forEach(function (d) {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    slice(document.querySelectorAll('[data-eb-region][open]')).forEach(function (d) { d.removeAttribute('open'); });
  });

  /* ══ A DISCLOSURE THAT HOLDS AN ERROR OPENS ITSELF ═════════════════════
     `form` is only in scope inside a form tag, so a <details> wrapping one
     cannot ask whether the submission failed. It does not need to: if the
     answer is in there, open it. */
  slice(document.querySelectorAll('details[data-eb-open-on-alert]')).forEach(function (d) {
    if (d.querySelector('.eb-alert--danger, .eb-alert--success')) d.setAttribute('open', '');
  });

  /* ══ FOOTER ACCORDION · open above 768, an accordion below ═════════════ */
  var narrow = window.matchMedia('(max-width: 768px)');
  function footerCols() {
    slice(document.querySelectorAll('[data-eb-footer-col]')).forEach(function (col, n) {
      if (narrow.matches) { if (!col._ebTouched) col.removeAttribute('open'); }
      else col.setAttribute('open', '');
    });
  }
  document.addEventListener('toggle', function (e) {
    var col = e.target.closest && e.target.closest('[data-eb-footer-col]');
    if (col && narrow.matches) col._ebTouched = true;
  }, true);
  narrow.addEventListener('change', footerCols);
  footerCols();

  /* ══ CART COUNT · other scripts announce a change; the badge follows ═══ */
  document.addEventListener('eb:cart:update', function (e) {
    var n = e.detail && e.detail.item_count;
    slice(document.querySelectorAll('[data-eb-cart-count]')).forEach(function (b) {
      if (typeof n !== 'number') return;
      b.textContent = n; b.hidden = n === 0; b.classList.toggle('is-live', n > 0);
      b.classList.remove('is-bumped'); void b.offsetWidth; b.classList.add('is-bumped');
    });
  });

  document.addEventListener('shopify:section:load', function () { footerCols(); });


  /* ══ MENU HIGHLIGHT · 角标按访客的时钟重算 ═══════════════════════════════
     服务器算的角标会跟着页面缓存一起旧掉（缓存里可能还写着 3D），所以打开页面时
     按 data-hl-start / data-hl-end 再算一次；过了结束时间整项拿掉。每分钟一次就够，
     最小单位是小时。 */
  function navHighlight() {
    var now = Date.now() / 1000;
    slice(document.querySelectorAll('[data-eb-hl]')).forEach(function (a) {
      var end = +a.getAttribute('data-hl-end') || 0;
      if (end && now > end) { a.remove(); return; }
      var badge = a.querySelector('[data-eb-hl-badge]');
      var start = +a.getAttribute('data-hl-start') || 0;
      if (!badge || !start) return;
      var left = start - now, txt, state = 'soon';
      if (left >= 86400) txt = badge.getAttribute('data-days').replace('%n%', Math.floor(left / 86400));
      else if (left > 0) txt = badge.getAttribute('data-hours').replace('%n%', Math.ceil(left / 3600));
      else { txt = badge.getAttribute('data-live'); state = 'live'; }
      if (badge.textContent !== txt) badge.textContent = txt;
      badge.setAttribute('data-state', state);
    });
  }
  navHighlight();
  setInterval(navHighlight, 60000);
  document.addEventListener('shopify:section:load', navHighlight);
})();
  /* ── the picker ──────────────────────────────────────────────────────────
     A <details> whose rows are radio inputs. The radios do the work — they
     submit with the form and they carry the checked state — so this only
     writes the chosen label onto the trigger and closes the panel. With the
     script gone the field still opens, still chooses and still submits; the
     trigger simply keeps whatever label the server rendered. */
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-eb-picker] input[type="radio"]');
    if (!input) return;
    var picker = input.closest('[data-eb-picker]');
    var out = picker.querySelector('[data-eb-picker-value]');
    var name = input.closest('.eb-menu__row').querySelector('.eb-menu__name');
    if (out && name) out.textContent = name.textContent.trim();
    picker.open = false;
  });


  /* ── the hero deck ───────────────────────────────────────────────────────
     Only runs where there is more than one slide, because a deck of one is a
     hero. Autoplay is OFF unless the merchant asked for it and stops for good
     the moment somebody chooses a slide themselves — a carousel that keeps
     moving after you have taken hold of it is arguing with you. */
  function bindHero(deck) {
    var slides = [].slice.call(deck.querySelectorAll('[data-eb-slide]'));
    var dots = [].slice.call(deck.querySelectorAll('.eb-hero__dot'));
    var sets = [].slice.call(deck.querySelectorAll('[data-eb-stops]'));
    if (slides.length < 2) return;
    var at = 0, timer = null;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function show(n) {
      at = (n + slides.length) % slides.length;
      slides.forEach(function (s, i) {
        s.classList.toggle('is-on', i === at);
        if (i === at) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
      });
      dots.forEach(function (d, i) {
        d.classList.toggle('is-on', i === at);
        d.setAttribute('aria-selected', i === at ? 'true' : 'false');
      });
      /* Each slide carries its own readings; the set moves with the slide. */
      sets.forEach(function (s, i) {
        s.classList.toggle('is-on', i === at);
        if (i === at) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
      });
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() {
      if (reduce || deck.dataset.autoplay !== 'true') return;
      stop();
      timer = setInterval(function () { show(at + 1); }, parseInt(deck.dataset.dwell, 10) || 8000);
    }

    dots.forEach(function (d, i) {
      d.addEventListener('click', function () { stop(); show(i); });
    });
    /* Pressing play is taking hold of it too. A deck that advances off a
       video somebody just started leaves the video playing to nobody, out of
       sight and still making noise. */
    deck.addEventListener('click', function (e) {
      if (e.target.closest('[data-eb-facade-play]')) stop();
    });
    deck.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (!e.target.closest('.eb-hero__dot')) return;
      stop();
      show(at + (e.key === 'ArrowRight' ? 1 : -1));
      dots[at].focus();
    });
    /* ON A PHONE A DECK IS SWIPED, not aimed at. The pager is a graduation
       three pixels tall and a thumb is nine millimetres; a deck whose only
       control is a tap on a hairline is a deck most phone visitors never
       move at all. Same rule as the dots: taking hold of it stops the
       autoplay for good.

       passive, and never preventDefault — the page still has to scroll under
       the finger, and a hero that eats a vertical drag is a hero nobody can
       scroll past. So the gesture has to prove it MEANT sideways: 40px at
       least, and half again more across than down. */
    var sx = 0, sy = 0, swiping = false;
    deck.addEventListener('touchstart', function (e) {
      swiping = e.touches.length === 1;
      if (!swiping) return;
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
    }, { passive: true });
    deck.addEventListener('touchend', function (e) {
      if (!swiping) return;
      swiping = false;
      var dx = e.changedTouches[0].clientX - sx;
      var dy = e.changedTouches[0].clientY - sy;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      stop();
      show(at + (dx < 0 ? 1 : -1));
    }, { passive: true });

    /* A slide nobody can see should not be playing to nobody. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden || !deck.isConnected) stop(); else start();
    });
    /* In the editor, choosing a slide shows it — its readings included — so
       the merchant checks slide three's numbers without chasing the pager. */
    document.addEventListener('shopify:block:select', function (e) {
      var i = slides.indexOf(e.target);
      if (i < 0) return;
      stop();
      show(i);
    });
    start();
  }
  [].slice.call(document.querySelectorAll('[data-eb-hero]')).forEach(bindHero);
  document.addEventListener('shopify:section:load', function (e) {
    [].slice.call(e.target.querySelectorAll('[data-eb-hero]')).forEach(bindHero);
  });


  /* Print, which is how a browser writes a PDF. print.css lays the sheet out
     for paper; this only asks for the dialog. */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-eb-print]')) window.print();
  });


  /* 防随手存图：图片上的右键菜单和拖拽不响应，别处的右键照常（复制文字、
     新标签打开链接都还要用）。截图挡不住，这只拦最省事的那一步。
     开关在 <html data-protect>，编辑器里是 off，免得挡住选区块。 */
  if (document.documentElement.getAttribute('data-protect') === 'on') {
    ['contextmenu', 'dragstart'].forEach(function (type) {
      document.addEventListener(type, function (e) {
        if (e.target.closest && e.target.closest('img, picture, video')) e.preventDefault();
      });
    });
  }
